import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import { authContext } from '@staffedupusa/shared-components';
import { mapPlanStatusAndColor } from '@utils/companyHelper';
import { FC, useContext, useEffect, useState } from 'react';

type CompanyDropdownModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (company: any) => void;
};

const CompanyDropdownModal: FC<CompanyDropdownModalProps> = ({
  isOpen,
  handleClose,
  handleSubmit,
}) => {
  const [applicationsListAlertText, setApplicationsListAlertText] =
    useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const { authData } = useContext(authContext);
  const [companiesData, setCompaniesData] = useState<any[]>([]);

  const { metricsData, isLoadingMetrics } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });

  useEffect(() => {
    if (metricsData?.companies) {
      const updatedCompaniesData = mapPlanStatusAndColor(metricsData.companies);
      setCompaniesData(updatedCompaniesData);
    }
  }, [metricsData]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCompany(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <BusinessIcon sx={{ fontSize: 60, color: 'primary', margin: 'auto' }} />
        <br />
        <Typography variant="h4" sx={{ p: 3 }}>
          Select Company For Adding Block
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mb: 3 }}>
          <Box sx={{ width: '100%' }}>
            {applicationsListAlertText && !isLoadingMetrics && (
              <Alert severity="error">{applicationsListAlertText}</Alert>
            )}
            {isLoadingMetrics && !applicationsListAlertText && (
              <CircularProgress />
            )}
            {companiesData.length === 0 &&
            !isLoadingMetrics &&
            !applicationsListAlertText ? (
              <Alert severity="error">No Companies Found</Alert>
            ) : (
              <Autocomplete
                size="small"
                options={companiesData}
                getOptionLabel={(option) =>
                  `${option.name}, ${option?.s_city}, ${option?.s_state}`
                }
                value={selectedCompany ?? null}
                onChange={(_, newValue) => {
                  setSelectedCompany(newValue ?? null);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.company_id === value?.company_id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Company"
                    size="small"
                    style={{ fontSize: '14px', width: '100%' }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.company_id}>
                    {option.name}, {option?.s_city}, {option?.s_state}
                    <span
                      style={{
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: '600',
                        backgroundColor: option.color,
                        marginLeft: '10px',
                        padding: '1px 5px 2px',
                        borderRadius: 5,
                      }}
                    >
                      {option.plan_status}
                    </span>
                  </li>
                )}
                getOptionDisabled={(option) => option.plan_status === 'Expired'}
              />
            )}
          </Box>
          <Button
            variant="contained"
            disabled={!selectedCompany}
            sx={{
              mt: 3,
              width: '100%',
              alignSelf: 'center',
              textAlign: 'center',
            }}
            startIcon={<DoneIcon />}
            onClick={() => {
              handleSubmit(selectedCompany);
              setSelectedCompany('');
            }}
          >
            Next
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDropdownModal;
